/** @format */

// Session cache of the catalog.
// - Light index of every active product (fast route /products/v2/products/), with the brand of
//   each product: enough to filter, count and search without waiting.
// - Full product (admin route) loaded card by card, when the card becomes visible.
// - Criteria of the chosen typology: one request, cached between visits.
// - Inactive products: loaded on demand.
import { useEffect, useSyncExternalStore } from 'react';

import { api } from '../../../../../api.ts';
import { cutByBrand, normalizeText } from './catalogLogic';

const INDEX_PAGE_SIZE = 500;
const ADMIN_PAGE_SIZE = 50;
const BRAND_CONCURRENCY = 2;
const DETAIL_CONCURRENCY = 3;
const PRICE_CONCURRENCY = 2;
const PRICES_WAIT_MS = 6000;
const LIGHT = '/products/v2/products/?is_visible_in_catalog=true';
const ADMIN =
  '/products/v2/admin-products/?is_archived=false&is_visible_in_catalog=true';

function createStore(initial) {
  let state = initial;
  const listeners = new Set();
  return {
    get: () => state,
    set: (updater) => {
      state = typeof updater === 'function' ? updater(state) : updater;
      listeners.forEach((listener) => listener());
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

// Runs `task(item)` for each item with at most `concurrency` calls at a time.
export async function runPool(items, concurrency, task) {
  const results = new Array(items.length);
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const index = next;
      next += 1;
      // eslint-disable-next-line no-await-in-loop
      results[index] = await task(items[index], index);
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(concurrency, items.length) }, worker),
  );
  return results;
}

async function fetchAllLight(params = '') {
  const url = (page) =>
    `${LIGHT}${params}&page_size=${INDEX_PAGE_SIZE}&page=${page}`;
  const first = await api.get(url(1));
  const pages = Math.ceil((first.data.count || 0) / INDEX_PAGE_SIZE);
  const rest = await Promise.all(
    Array.from({ length: Math.max(0, pages - 1) }, (_, i) =>
      api.get(url(i + 2)),
    ),
  );
  return {
    count: first.data.count || 0,
    items: [first, ...rest].flatMap((r) => r.data.results || []),
  };
}

// --- References (typologies, brands, sub-categories) ---

// --- Session-to-session cache (localStorage) ---
// Typologies, brands, sub-categories and the light index change rarely: they show at once from
// the last visit, then refresh in the background. No price, coefficient nor formula is stored.

const CACHE_KEY = 'catalog-index-cache-v1';

function readCache() {
  try {
    const cache = JSON.parse(localStorage.getItem(CACHE_KEY));
    return cache?.items?.length && cache.refs ? cache : null;
  } catch (err) {
    return null;
  }
}

function writeCache() {
  // eslint-disable-next-line no-use-before-define
  const refs = refsStore.get();
  // eslint-disable-next-line no-use-before-define
  const index = indexStore.get();
  const pick = (list, keys) =>
    list.map((o) => Object.fromEntries(keys.map((k) => [k, o?.[k] ?? null])));
  try {
    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({
        ts: Date.now(),
        refs: {
          typologies: pick(refs.typologies, ['slug', 'name']),
          brands: pick(refs.brands, ['id', 'name', 'image']),
          subCategories: pick(refs.subCategories, ['slug', 'name']),
        },
        items: index.items.map((p) => ({
          ...pick(
            [p],
            [
              'id',
              'public_name',
              'visual_name',
              'internal_name',
              'prefixed_internal_name',
              'is_validated',
              'brand',
            ],
          )[0],
          typology: pick([p.typology], ['slug', 'name'])[0],
          sub_categories: pick(p.sub_categories || [], ['slug', 'name']),
        })),
      }),
    );
  } catch (err) {
    // Storage full or disabled: the page works without the cache.
  }
}

const cached = readCache();

const refsStore = createStore(
  cached
    ? { status: 'done', stale: true, ...cached.refs, error: null }
    : {
        status: 'idle',
        typologies: [],
        brands: [],
        subCategories: [],
        error: null,
      },
);
let refsPromise = null;

export function loadRefs(force = false) {
  const { status, stale } = refsStore.get();
  if (!force && refsPromise && (status === 'loading' || stale)) {
    return refsPromise;
  }
  if (!force && status === 'done' && !stale) return Promise.resolve();
  // Cached references stay on screen while they refresh.
  if (!stale) refsStore.set((s) => ({ ...s, status: 'loading', error: null }));
  refsPromise = Promise.all([
    api.get('/products/v2/typologies/?page_size=1000'),
    api.get('/products/v2/brands/?page_size=1000'),
    api.get('/products/v2/sub-categories/?page_size=1000'),
  ])
    .then(([typologies, brands, subCategories]) => {
      refsStore.set({
        status: 'done',
        typologies: typologies.data.results || [],
        brands: brands.data.results || [],
        subCategories: subCategories.data.results || [],
        error: null,
      });
    })
    .catch(() => {
      if (refsStore.get().stale) return;
      refsStore.set((s) => ({
        ...s,
        status: 'error',
        error: 'Impossible de charger les typologies et les marques.',
      }));
    });
  return refsPromise;
}

export function useCatalogRefs() {
  useEffect(() => {
    loadRefs();
  }, []);
  return useSyncExternalStore(refsStore.subscribe, refsStore.get);
}

// --- Light index ---

const indexStore = createStore(
  cached
    ? {
        status: 'ready',
        stale: true,
        items: cached.items.map((p) => ({
          ...p,
          is_active: true,
          is_archived: false,
        })),
        firstPage: [],
        brandsStatus: 'ready',
        error: null,
      }
    : {
        status: 'idle',
        items: [],
        firstPage: [],
        brandsStatus: 'idle',
        error: null,
      },
);

const sleep = (ms) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

// Resolves when the visible cards are loaded (or after `maxMs`), so they go first.
async function whenCardsLoaded(maxMs) {
  const start = Date.now();
  // eslint-disable-next-line no-use-before-define
  while ((detailsRunning || detailQueue.length) && Date.now() - start < maxMs) {
    // eslint-disable-next-line no-await-in-loop
    await sleep(100);
  }
}

async function brandsOneByOne(brands, noBrand) {
  const brandOf = {};
  await runPool(brands, BRAND_CONCURRENCY, async (brand) => {
    const { count, items } = await fetchAllLight(
      `&brand=${brand.id}&ordering=id`,
    );
    if (count === noBrand.count && items[0]?.id === noBrand.firstId) return;
    items.forEach((p) => {
      brandOf[p.id] = brand.id;
    });
  });
  return brandOf;
}

async function fetchBrandOf(brands, byBrandOrder) {
  // The light route answers an empty brand with the products without brand: brand=0 shows
  // that answer, to recognise it.
  const probe = await api.get(`${LIGHT}&brand=0&page_size=1&ordering=id`);
  const noBrand = {
    count: probe.data.count || 0,
    firstId: probe.data.results?.[0]?.id,
  };
  const heads = await runPool(brands, BRAND_CONCURRENCY, (brand) =>
    api
      .get(`${LIGHT}&brand=${brand.id}&page_size=1&ordering=id`)
      .then((res) => {
        const count = res.data.count || 0;
        const firstId = res.data.results?.[0]?.id;
        const empty = count === noBrand.count && firstId === noBrand.firstId;
        return { brandId: brand.id, count: empty ? 0 : count, firstId };
      }),
  );
  return (
    (byBrandOrder && cutByBrand(byBrandOrder, heads, noBrand.count)) ||
    brandsOneByOne(brands, noBrand)
  );
}

async function loadBrandsOfProducts(brands, byBrandOrder) {
  indexStore.set((s) => ({ ...s, brandsStatus: 'loading' }));
  try {
    const brandOf = await fetchBrandOf(brands, byBrandOrder);
    indexStore.set((s) => ({
      ...s,
      brandsStatus: 'ready',
      items: s.items.map((p) => ({
        ...p,
        brand: brandOf[p.id] ? { id: brandOf[p.id] } : null,
      })),
    }));
    writeCache();
  } catch (err) {
    indexStore.set((s) => ({ ...s, brandsStatus: 'error' }));
  }
}

function makeNormalize(subCategories) {
  const subCategoriesBySlug = Object.fromEntries(
    subCategories.map((sc) => [sc.slug, sc]),
  );
  return (p) => ({
    ...p,
    is_active: true,
    is_archived: false,
    brand: null,
    sub_categories: (p.sub_categories || []).map((sc) =>
      typeof sc === 'string'
        ? subCategoriesBySlug[sc] || { slug: sc, name: sc }
        : sc,
    ),
  });
}

// Index shown from the cache: reload it quietly and swap it in one go.
let refreshing = null;

async function refreshIndex() {
  // eslint-disable-next-line no-use-before-define
  openPrices();
  try {
    await loadRefs();
    const { brands, subCategories } = refsStore.get();
    await whenCardsLoaded(3000);
    const { items } = await fetchAllLight('&ordering=brand,id');
    const brandOf = await fetchBrandOf(brands, items);
    const normalize = makeNormalize(subCategories);
    indexStore.set((s) => ({
      ...s,
      stale: false,
      status: 'ready',
      brandsStatus: 'ready',
      items: [...items]
        .sort((p, q) => p.id - q.id)
        .map((p) => ({
          ...normalize(p),
          brand: brandOf[p.id] ? { id: brandOf[p.id] } : null,
        })),
    }));
    writeCache();
  } catch (err) {
    // The cached index stays; the next visit retries.
  }
}

export async function loadIndex({ brands, subCategories }, force = false) {
  const { status, stale } = indexStore.get();
  if (!force && stale) {
    refreshing = refreshing || refreshIndex();
    return;
  }
  if (!force && (status === 'loading' || status === 'ready')) return;
  indexStore.set((s) => ({ ...s, status: 'loading', error: null }));
  // eslint-disable-next-line no-use-before-define
  setTimeout(openPrices, PRICES_WAIT_MS);
  const normalize = makeNormalize(subCategories);
  let byBrandOrder;
  try {
    // 1. First cards right away.
    const first = await api.get(`${LIGHT}&page_size=24`);
    indexStore.set((s) => ({
      ...s,
      firstPage: (first.data.results || []).map(normalize),
    }));
    // 2. Then the whole index: typologies and sub-categories with their counts.
    await whenCardsLoaded(2000);
    // Ordered by brand, so that the brand of each product can be found with one count per brand.
    ({ items: byBrandOrder } = await fetchAllLight('&ordering=brand,id'));
    indexStore.set((s) => ({
      ...s,
      status: 'ready',
      items: [...byBrandOrder].sort((p, q) => p.id - q.id).map(normalize),
      firstPage: [],
    }));
  } catch (err) {
    indexStore.set((s) => ({
      ...s,
      status: 'error',
      error: 'Le chargement du catalogue a échoué. Vérifiez la connexion.',
    }));
    // eslint-disable-next-line no-use-before-define
    openPrices();
    return;
  }
  // 3. Then the prices of the visible cards and the brand of each product.
  // eslint-disable-next-line no-use-before-define
  openPrices();
  loadBrandsOfProducts(brands, byBrandOrder);
}

export function useCatalogIndex(refs) {
  useEffect(() => {
    if (refs.status === 'done') loadIndex(refs);
  }, [refs.status]);
  return useSyncExternalStore(indexStore.subscribe, indexStore.get);
}

export function retryIndex() {
  const refs = refsStore.get();
  if (indexStore.get().status === 'ready') {
    loadBrandsOfProducts(refs.brands, null);
  } else loadIndex(refs, true);
}

// --- Full products (admin route), card by card ---

const details = new Map(); // id -> full product
const detailPromises = new Map();
const detailListeners = new Set();
const detailQueue = [];
let detailsRunning = 0;

function storeDetails(list) {
  list.forEach((p) => details.set(p.id, p));
  detailListeners.forEach((listener) => listener());
}

function startDetail({ id, resolve }) {
  detailsRunning += 1;
  api
    .get(`/products/v2/admin-products/${id}/`)
    .then((res) => storeDetails([res.data]))
    .catch(() => detailPromises.delete(id))
    .finally(() => {
      detailsRunning -= 1;
      resolve();
      // eslint-disable-next-line no-use-before-define
      pumpDetails();
    });
}

function pumpDetails() {
  while (detailsRunning < DETAIL_CONCURRENCY && detailQueue.length) {
    startDetail(detailQueue.shift());
  }
}

export function requestDetail(id) {
  if (details.has(id)) return Promise.resolve();
  if (!detailPromises.has(id)) {
    detailPromises.set(
      id,
      new Promise((resolve) => {
        detailQueue.push({ id, resolve });
        pumpDetails();
      }),
    );
  }
  return detailPromises.get(id);
}

export function getDetail(id) {
  return details.get(id);
}

function subscribeDetails(listener) {
  detailListeners.add(listener);
  return () => detailListeners.delete(listener);
}

export function useProductDetail(id, enabled) {
  useEffect(() => {
    if (enabled && id) requestDetail(id);
  }, [enabled, id]);
  return useSyncExternalStore(subscribeDetails, () => details.get(id));
}

async function fetchAdminPages(params, onPage, shouldStop) {
  let page = 1;
  for (;;) {
    // eslint-disable-next-line no-await-in-loop
    const res = await api.get(
      `${ADMIN}${params}&page=${page}&page_size=${ADMIN_PAGE_SIZE}`,
    );
    const results = res.data.results || [];
    storeDetails(results);
    onPage(results, res.data.count || 0);
    if (!res.data.next || (shouldStop && shouldStop(results))) return;
    page += 1;
  }
}

// --- Criteria of a typology: one request, kept between visits, refreshed in the background ---
// Only the criterion fields and the ids of the documents are kept: no price nor formula.

const CRITERIA_CACHE_KEY = 'catalog-criteria-cache-v1';
const DOC_FIELDS = [
  'data_sheets',
  'conformity_certificates',
  'warranties',
  'user_manuals',
];

function readCriteriaCache() {
  try {
    return JSON.parse(localStorage.getItem(CRITERIA_CACHE_KEY)) || {};
  } catch (err) {
    return {};
  }
}

// Criterion fields and documents of a full product, enough to filter it.
function slimProduct(product, fields) {
  const detailsKey = Object.keys(product).find(
    (k) =>
      k.endsWith('_details') && product[k] && typeof product[k] === 'object',
  );
  return {
    image: product.image || null,
    ...Object.fromEntries(
      DOC_FIELDS.map((f) => [f, (product[f] || []).map((d) => ({ id: d.id }))]),
    ),
    ...(detailsKey && {
      [detailsKey]: Object.fromEntries(
        fields.map((f) => [f, product[detailsKey][f] ?? null]),
      ),
    }),
  };
}

// slug -> { status: 'loading' | 'complete' | 'error', stale, byId: { id -> slim product } }
const criteriaStore = createStore(
  Object.fromEntries(
    Object.entries(readCriteriaCache()).map(([slug, byId]) => [
      slug,
      { status: 'complete', stale: true, byId },
    ]),
  ),
);

export function hasCriteriaCache(slug) {
  return Boolean(criteriaStore.get()[slug]?.stale);
}

export function loadTypologyDetails(slug, fields) {
  const entry = criteriaStore.get()[slug];
  if (!slug || entry?.refreshing) return;
  if (entry && !entry.stale && entry.status !== 'error') return;
  criteriaStore.set((s) => ({
    ...s,
    [slug]: entry?.stale
      ? { ...entry, refreshing: true }
      : { status: 'loading', byId: {} },
  }));
  api
    .get(`${ADMIN}&typology=${encodeURIComponent(slug)}&page_size=1000`)
    .then((res) => {
      const results = res.data.results || [];
      storeDetails(results);
      const byId = Object.fromEntries(
        results.map((p) => [p.id, slimProduct(p, fields)]),
      );
      criteriaStore.set((s) => ({
        ...s,
        [slug]: { status: 'complete', byId },
      }));
      try {
        localStorage.setItem(
          CRITERIA_CACHE_KEY,
          JSON.stringify({ ...readCriteriaCache(), [slug]: byId }),
        );
      } catch (err) {
        // Storage full or disabled: the page works without the cache.
      }
    })
    .catch(() =>
      criteriaStore.set((s) => ({
        ...s,
        // Cached criteria stay on screen if the refresh fails.
        [slug]: s[slug]?.stale
          ? { ...s[slug], refreshing: false, stale: false }
          : { status: 'error', byId: {} },
      })),
    );
}

export function useTypologyDetails(slug, fields, enabled) {
  useEffect(() => {
    if (enabled) loadTypologyDetails(slug, fields);
  }, [slug, enabled]);
  return useSyncExternalStore(criteriaStore.subscribe, criteriaStore.get);
}

// --- Inactive products, on demand ---

const inactiveStore = createStore({ status: 'idle', items: [] });

export function loadInactive(force = false) {
  const { status } = inactiveStore.get();
  if (!force && (status === 'loading' || status === 'complete')) return;
  inactiveStore.set({ status: 'loading', items: [] });
  const items = [];
  // Inactive first, then stop at the first page that reaches the active products.
  fetchAdminPages(
    '&ordering=is_active,-id',
    (results) => {
      items.push(...results.filter((p) => !p.is_active));
      inactiveStore.set({ status: 'loading', items: [...items] });
    },
    (results) => results.some((p) => p.is_active),
  )
    .then(() => inactiveStore.set({ status: 'complete', items }))
    .catch(() => inactiveStore.set({ status: 'error', items }));
}

export function useInactiveProducts(enabled) {
  useEffect(() => {
    if (enabled) loadInactive();
  }, [enabled]);
  return useSyncExternalStore(inactiveStore.subscribe, inactiveStore.get);
}

// --- Search on the server (manufacturer references are not in the light index) ---

const searchStore = createStore({}); // normalized query -> { status, ids }

export function useSearchIds(search, enabled) {
  const key = normalizeText(search);
  useEffect(() => {
    if (!enabled || searchStore.get()[key]) return;
    searchStore.set((s) => ({ ...s, [key]: { status: 'loading' } }));
    fetchAllLight(`&search=${encodeURIComponent(search.trim())}`)
      .then(({ items }) =>
        searchStore.set((s) => ({
          ...s,
          [key]: { status: 'done', ids: new Set(items.map((p) => p.id)) },
        })),
      )
      .catch(() =>
        searchStore.set((s) => ({ ...s, [key]: { status: 'error' } })),
      );
  }, [key, enabled]);
  const all = useSyncExternalStore(searchStore.subscribe, searchStore.get);
  return enabled ? all[key] : undefined;
}

// --- Typology detail (price formula, not in the typologies list) ---

const typologyStore = createStore({});

export function useTypologyDetail(slug, enabled = true) {
  useEffect(() => {
    if (!enabled || !slug || typologyStore.get()[slug]) return;
    typologyStore.set((s) => ({ ...s, [slug]: { status: 'loading' } }));
    api
      .get(`/products/v2/typologies/${slug}/`)
      .then((res) =>
        typologyStore.set((s) => ({
          ...s,
          [slug]: { status: 'done', data: res.data },
        })),
      )
      .catch(() =>
        typologyStore.set((s) => ({ ...s, [slug]: { status: 'error' } })),
      );
  }, [slug, enabled]);
  const all = useSyncExternalStore(typologyStore.subscribe, typologyStore.get);
  return slug ? all[slug] : undefined;
}

// --- Selling prices, card by card ---

const prices = new Map(); // id -> { status: 'loading' | 'done' | 'error', value }
const priceListeners = new Set();
const priceQueue = [];
const pricePromises = new Map();
let pricesRunning = 0;

function setPrice(id, value) {
  prices.set(id, value);
  priceListeners.forEach((listener) => listener());
}

function startPrice({ id, resolve }) {
  pricesRunning += 1;
  api
    .post(`/products/v2/products/${id}/dynamic_price/`, {}, {})
    .then((res) => {
      const value = Number(res.data?.price);
      setPrice(
        id,
        Number.isFinite(value) && value > -1
          ? { status: 'done', value }
          : { status: 'error' },
      );
    })
    .catch(() => setPrice(id, { status: 'error' }))
    .finally(() => {
      pricesRunning -= 1;
      resolve();
      // eslint-disable-next-line no-use-before-define
      pumpPrices();
    });
}

// Prices wait for the index (filters first), at most PRICES_WAIT_MS.
let pricesOpen = false;

function openPrices() {
  pricesOpen = true;
  // eslint-disable-next-line no-use-before-define
  pumpPrices();
}

function pumpPrices() {
  while (pricesOpen && pricesRunning < PRICE_CONCURRENCY && priceQueue.length) {
    startPrice(priceQueue.shift());
  }
}

export function requestPrice(id) {
  if (!pricePromises.has(id)) {
    setPrice(id, { status: 'loading' });
    pricePromises.set(
      id,
      new Promise((resolve) => {
        priceQueue.push({ id, resolve });
        pumpPrices();
      }),
    );
  }
  return pricePromises.get(id);
}

function subscribePrices(listener) {
  priceListeners.add(listener);
  return () => priceListeners.delete(listener);
}

export function usePrice(product, enabled) {
  const id = product?.id;
  const active = Boolean(product?.is_active);
  useEffect(() => {
    if (enabled && active && id) requestPrice(id);
  }, [enabled, active, id]);
  return useSyncExternalStore(subscribePrices, () => prices.get(id));
}

// --- Labels and choices of the `<typology>_details` fields (OPTIONS, product managers only) ---

const schemas = createStore({});

export function useDetailsSchema(slug) {
  useEffect(() => {
    if (!slug || schemas.get()[slug]) return;
    schemas.set((s) => ({ ...s, [slug]: { status: 'loading' } }));
    api
      .options(`/products/v2/admin-products/?typology_id=${slug}`)
      .then((res) => {
        const actions = res.data?.actions?.POST || {};
        const key = Object.keys(actions).find(
          (k) => k.endsWith('_details') && actions[k].type === 'nested object',
        );
        const labels = {};
        const choices = {};
        Object.entries(actions[key]?.children || {}).forEach(
          ([name, field]) => {
            labels[name] = field.label || name;
            if (Array.isArray(field.choices)) {
              choices[name] = Object.fromEntries(
                field.choices.map((c) => [c.value, c.display_name]),
              );
            }
          },
        );
        schemas.set((s) => ({
          ...s,
          [slug]: { status: 'done', labels, choices },
        }));
      })
      .catch(() => schemas.set((s) => ({ ...s, [slug]: { status: 'error' } })));
  }, [slug]);
  const all = useSyncExternalStore(schemas.subscribe, schemas.get);
  return slug ? all[slug] : undefined;
}

// Drops the cached data of an edited product so that it reloads.
export function forgetProduct(product) {
  details.delete(product.id);
  detailPromises.delete(product.id);
  prices.delete(product.id);
  pricePromises.delete(product.id);
  const slug = product.typology?.slug;
  if (slug) criteriaStore.set((s) => ({ ...s, [slug]: undefined }));
  storeDetails([]);
}
